import { TILE_SIZE, ticks } from './config';
import { PICKUP, PickupKind, ROYALE_TIERS } from './economy';
import { Ground, groundAt, type MapDef } from './map';
import { ROD } from './weapons';

/**
 * Fishing in a battle royale (ADR-0229): a rod is a secondary (`Explosive.Rod`, its baits counted as grenades are); the
 * right button casts its float at the crosshair, at most `reach` px, onto a map's fishing water (`fishable`): flying
 * `castBase` s plus a second a `castSpeed` px, `castArc` px high at its middle. It bites `bite` s later (a nibble or two
 * first, `nibbles` at most); the right button within `hookTicks` hooks it, and what is on the line is drawn then
 * (`FISH_PRIZES`, the world's secret). Then the fight (`FISH_FIGHT` by its tier): held, the right button reels it in at
 * `reel` px/s over its weight and the line's tension rises to `held`; let go, it falls to `slack`. Now and then it runs:
 * warned `warnTicks` before, it pulls the tension towards `runHeld` over its power while the button is held (snapping the
 * line at 1), to `runFree` while it is let go, taking `give` px/s back. Never reeled between its runs for `slackTicks`,
 * it spits the hook. Walked `snap` px further than `reach` from it, the line snaps; a shot cuts it. Reeled in, it bursts
 * out of the water and flies `outTicks` on the line, `outHigh` px up, to the grass beside its gardener, flops there
 * `flopTicks`, and bursts into its prize. A catch, a fish lost or a line cut spends a bait; the last spent, the rod is.
 * Tension and slack are fractions (1: the line snaps); the per-second numbers the sim scales by its tick.
 */
export const FISHING = {
  baits: ROD.baits,
  reach: 130,
  snap: 34,
  castBase: 0.35,
  castSpeed: 260,
  castArc: 30,
  bite: [2.2, 5.5],
  nibbles: 2,
  hookTicks: ticks(0.65),
  reel: 55,
  give: 14,
  rise: 2.4,
  fall: 3.6,
  held: 0.55,
  slack: 0.1,
  runHeld: 0.25,
  runFree: 0.55,
  warnTicks: ticks(0.45),
  slackTicks: ticks(2.2),
  /** A fight starts at least this long a line (px), however near the float lay. */
  minLine: 70,
  /** How far a run pulls the fish aside (px/s, at most `aside` px). */
  drift: 34,
  aside: 38,
  outTicks: ticks(0.45),
  outHigh: 30,
  flopTicks: ticks(0.4),
} as const;

/** A fishing line's legs (ADR-0229: the sim's `Line.phase`, the wire's `fish.phase`); `None`: no line out. */
export const FishPhase = { None: 0, Cast: 1, Wait: 2, Bite: 3, Fight: 4, Out: 5, Flop: 6 } as const;
export type FishPhase = (typeof FishPhase)[keyof typeof FishPhase];

/** The tier of what is on a line (ADR-0229): its fish's colour, and its prize's. */
export const FishTier = { None: 0, Common: 1, Uncommon: 2, Epic: 3 } as const;
export type FishTier = (typeof FishTier)[keyof typeof FishTier];

/** What befalls a line (the `fish` event's `what`, ADR-0229). */
export const FishMoment = {
  Cast: 0,
  Plop: 1,
  Nibble: 2,
  Bite: 3,
  Hook: 4,
  Run: 5,
  Snap: 6,
  Gone: 7,
  Cut: 8,
  Out: 9,
  Land: 10,
  Catch: 11,
  Reeled: 12,
} as const;
export type FishMoment = (typeof FishMoment)[keyof typeof FishMoment];

/** A line's state on the wire (`fish.flags`): a run warned, a run pulling, its gardener reeling, its fish leaping. */
export const FishFlag = { Warned: 1, Running: 2, Reeling: 4, Breach: 8 } as const;

/**
 * How a tier's fish fights (ADR-0229): `weight` slows the reel (its speed over it), runs come every `gap` s (between),
 * last `len` s, pull `power` hard, and its fight starts `far` times further out; an Epic leaps once (`breach`, s into it).
 */
export const FISH_FIGHT: Readonly<
  Record<
    1 | 2 | 3,
    {
      weight: number;
      gap: readonly [number, number];
      len: readonly [number, number];
      power: number;
      far: number;
      breach: readonly [number, number] | null;
    }
  >
> = {
  [FishTier.Common]: { weight: 1, gap: [2, 3], len: [0.4, 0.6], power: 1, far: 1, breach: null },
  [FishTier.Uncommon]: { weight: 1.15, gap: [1.4, 2], len: [0.5, 0.75], power: 1.08, far: 1.05, breach: null },
  [FishTier.Epic]: { weight: 1.25, gap: [1.1, 1.6], len: [0.6, 0.85], power: 1.16, far: 1.1, breach: [0.8, 1.6] },
};

/**
 * What bites (ADR-0229), by weight out of their sum: a heal or a shield most of the time, beacons, an Ejector Seat, an
 * Uncommon gun or an Epic one (each of a tier's guns as likely). An Epic gun is six in a hundred: six times a chest's.
 */
export const FISH_PRIZES: readonly {
  readonly tier: 1 | 2 | 3;
  readonly weight: number;
  readonly pickup?: PickupKind;
  readonly amount?: number;
  readonly guns?: readonly string[];
}[] = [
  { tier: FishTier.Common, weight: 30, pickup: PickupKind.Heal, amount: 40 },
  { tier: FishTier.Common, weight: 30, pickup: PickupKind.Shield, amount: 40 },
  { tier: FishTier.Common, weight: 10, pickup: PickupKind.Beacon, amount: PICKUP.beacons },
  { tier: FishTier.Common, weight: 6, pickup: PickupKind.Ejector, amount: PICKUP.ejector },
  { tier: FishTier.Uncommon, weight: 18, guns: ROYALE_TIERS.uncommon.guns },
  { tier: FishTier.Epic, weight: 6, guns: ROYALE_TIERS.epic.guns },
];

/** The prize a roll `r` in [0, 1) draws (`FISH_PRIZES`), and which of its guns a second roll `g` in [0, 1) picks. */
export function fishPrize(
  r: number,
  g: number,
): { tier: 1 | 2 | 3; pickup?: PickupKind; amount?: number; gun?: string } {
  const total = FISH_PRIZES.reduce((n, p) => n + p.weight, 0);
  let left = r * total;
  for (const p of FISH_PRIZES) {
    left -= p.weight;
    if (left >= 0) continue;
    if (p.guns) return { tier: p.tier, gun: p.guns[Math.min(p.guns.length - 1, Math.floor(g * p.guns.length))] };
    return {
      tier: p.tier,
      ...(p.pickup !== undefined ? { pickup: p.pickup } : {}),
      ...(p.amount ? { amount: p.amount } : {}),
    };
  }
  return { tier: FishTier.Common, pickup: PickupKind.Heal, amount: 40 };
}

const water = (map: MapDef, x: number, y: number) =>
  groundAt(map, Math.floor(x / TILE_SIZE), Math.floor(y / TILE_SIZE)) === Ground.Water;

/**
 * Can a float land at (x, y) (ADR-0229)? Open water of one of the map's fishing waters (`MapDef.fishing`), clear of the
 * shores and of the cliffs under them (water up to 26 px north of it, 10 px either side).
 */
export function fishable(map: MapDef, x: number, y: number): boolean {
  if (!map.fishing.some(([x0, y0, x1, y1]) => x >= x0 && x < x1 && y >= y0 && y < y1)) return false;
  return (
    water(map, x, y) && water(map, x, y - 14) && water(map, x, y - 26) && water(map, x - 10, y) && water(map, x + 10, y)
  );
}
